<script lang="ts">
	import {
		CardGrid,
		CircleAvatar,
		DashSelectCard,
		Text,
		Stack,
		LabelV2,
	} from '@Shelly/shelly-design-system';
	import type {
		DashCameraWidgetVariant,
		DashSelectList,
		DashWidgetEditType,
		DashWidgetTypes,
		ListRefType,
	} from '../../dash-types';
	import { edit } from 'src/stores/ui/edit_lists';
	import { getWidgetTitleProposal } from '../../dash-helpers';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import { rooms } from 'src/stores/lists/rooms';
	import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
	import { scenes } from 'src/stores/lists/scenes';
	import { filterScenesInitial } from 'src/components/Lists/list-helpers';
	import { checkLightGroupHasSingleSlider } from '../../dash-lights-helpers';
	import { isCameraDevice } from 'src/helpers/interface_helpers';
	import { getDashWidgetSelectListRefKey } from '../../dash-camera-helpers';
	import { mapIconSolidWeightToLight } from 'src/helpers/new-layout-helpers';
	import { format } from 'svelte-i18n';
	import { hasCameraDevices } from 'src/components/Camera/helpers/camera-helpers';

	export let editItem: DashWidgetEditType;
	export let allCardsRefs: ListRefType = {};

	$: isPremium = $user_data.premium || $user_settings.been_premium;

	$: DASH_WIDGET_TYPES_LIST = [
		{
			title: $format('label_widget_devices', {
				default: 'Devices',
			}),
			icon: 'fal fa-microchip',
			selectCards: [
				// {
				// 	title: $format('label_devices_data_card', {
				// 		default: 'Data cards',
				// 	}),
				// 	additionalText: $format('label_devices_data_card_info', {
				// 		default: 'Displays device data only.',
				// 	}),
				// 	type: 'dashParameterDev',
				// 	disabled: Object.values($devices).length <= 0,
				// },
				{
					title: $format('label_devices_quick_card', {
						default: 'Control card',
					}),
					additionalText: $format('label_devices_quick_card_info', {
						default: 'Control the device by tapping anywhere on the card.',
					}),
					type: 'dashQuickDev',
					disabled: Object.values($devices).length <= 0,
				},
				{
					title: $format('label_devices_card', {
						default: 'Button card',
					}),
					additionalText: $format('label_devices_card_info', {
						default: 'Control the device using the button.',
					}),
					type: 'dashCardDev',
					disabled: Object.values($devices).length <= 0,
				},
				{
					title: $format('label_devices_control_card', {
						default: 'Horizontal button card',
					}),
					additionalText: $format('label_devices_control_card_info', {
						default: 'Control the device using the button.',
					}),
					type: 'dashControlDev',
					disabled: Object.values($devices).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_rooms', {
				default: 'Rooms',
			}),
			icon: 'fal fa-door-open',
			selectCards: [
				{
					title: $format('label_rooms_data_card', {
						default: 'Rooms',
					}),
					additionalText: '',
					type: 'dashRoom',
					disabled: Object.values($rooms).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_groups', {
				default: 'Groups',
			}),
			icon: 'fal fa-microchip',
			selectCards: [
				{
					title: $format('label_groups_data_card', {
						default: 'Groups',
					}),
					additionalText: '',
					type: 'dashGroup',
					disabled: Object.values($groups).length <= 0,
				},
				{
					title: $format('label_light_groups_data_card', {
						default: 'Light groups',
					}),
					additionalText: '',
					type: 'dashLight',
					disabled:
						Object.values($groups)
							.filter((group: any) => group.type === 'light')
							.filter((lightGroup) => checkLightGroupHasSingleSlider(lightGroup)).length <=
						0,
				},
			],
		},
		{
			title: $format('label_widget_scenes', {
				default: 'Scenes',
			}),
			icon: 'fal fa-microchip',
			selectCards: [
				{
					title: $format('label_scenes_data_card', {
						default: 'Scenes',
					}),
					additionalText: '',
					type: 'dashScene',
					disabled: Object.values($scenes).filter(filterScenesInitial).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_alarms', {
				default: 'Alarms',
			}),
			icon: 'fal fa-siren-on',
			selectCards: [
				{
					title: $format('label_alarms_data_card', {
						default: 'Alarms',
					}),
					additionalText: '',
					type: 'dashAlarm',
					disabled: Object.values($alarms).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_thermostats', {
				default: 'Thermostats',
			}),
			icon: 'fal fa-temperature-half',
			selectCards: [
				{
					title: $format('label_thermostats_data_card', {
						default: 'Thermostats',
					}),
					type: 'dashThermostat',
					disabled: Object.values($temp_schemes).length <= 0,
				},
			],
		},
		{
			title: $format('label_widget_energy', {
				default: 'Energy',
			}),
			icon: 'fal fa-bolt',
			selectCards: [
				{
					title: $format('label_energy_data_card', {
						default: 'Energy',
					}),
					additionalText: '',
					type: 'dashChart',
					disabled: false,
				},
			],
		},
		{
			title: $format('label_widget_cameras', {
				default: 'Cameras',
			}),
			icon: 'fal fa-video',
			selectCards: [
				{
					title: $format('label_camera_full_preview_card', {
						default: 'Full Width Preview',
					}),
					additionalText: $format('label_camera_full_preview_card_info', {
						default: 'Show up to four live camera previews.',
					}),
					type: 'dashCamera',
					cameraVariant: 'full-preview',
					image: './images/dash/dash-widget-dashCamera-full-preview.svg',
					bottomLabelIcon: 'fal fa-video',
					disabled: !hasCameraDevices($devices),
				},
				{
					title: $format('label_camera_triple_focus_card', {
						default: '3 Camera Layout',
					}),
					additionalText: $format('label_camera_triple_focus_card_info', {
						default: 'Focus one camera with two smaller previews.',
					}),
					type: 'dashCamera',
					cameraVariant: 'triple-focus',
					image: './images/dash/dash-widget-dashCamera-triple-focus.svg',
					bottomLabelIcon: 'fal fa-video',
					disabled: !hasCameraDevices($devices),
				},
			],
		},
		{
			title: $format('label_widget_special', {
				default: 'Special',
			}),
			icon: 'fak fa-light-sparkles',
			selectCards: [
				{
					title: $format('label_special_acc_info_data_card', {
						default: 'Account Information',
					}),
					additionalText: '',
					type: 'dashInfo',
					bottomLabelIcon: 'fal fa-user',
					disabled: false,
				},
				{
					title: $format('label_special_weather_data_card', {
						default: 'Weather',
					}),
					additionalText: '',
					type: 'dashWeather',
					bottomLabelIcon: 'fal fa-clouds',
					disabled: !isPremium,
					premium: true,
				},
				{
					title: $format('label_special_music_data_card', {
						default: 'Music Player',
					}),
					additionalText: '',
					type: 'dashMedia',
					bottomLabelIcon: 'fal fa-music',
					disabled:
						Object.values($devices).filter((device) => device?.category === 'media_player')
							.length <= 0,
				},
			],
		},
	] as DashSelectList;

	const onSelectWidgetType = (type: DashWidgetTypes, cameraVariant?: DashCameraWidgetVariant) => {
		editItem.selectItems = [];

		if (!$edit || editItem.widgetType !== type || editItem.cameraConfig?.variant !== cameraVariant) {
			editItem.widgetTitle = getWidgetTitleProposal(type);
		}

		editItem.widgetType = type;

		if (type === 'dashCamera') {
			const isSameCameraVariant = editItem.cameraConfig?.variant === cameraVariant;
			const feedMode =
				isSameCameraVariant && editItem.cameraConfig?.feedMode
					? editItem.cameraConfig.feedMode
					: 'live';
			editItem.cameraConfig = {
				variant: cameraVariant || editItem.cameraConfig?.variant || 'full-preview',
				feedMode,
				sideFeedMode:
					isSameCameraVariant && editItem.cameraConfig?.sideFeedMode
						? editItem.cameraConfig.sideFeedMode
						: 'preview',
				mainAlwaysLive:
					isSameCameraVariant && editItem.cameraConfig?.mainAlwaysLive !== undefined
						? editItem.cameraConfig.mainAlwaysLive
						: true,
			};
		} else {
			editItem.cameraConfig = undefined;
		}

		if (type === 'dashChart') {
			editItem.selectItems = [
				{
					id: 'overalConsumption',
					itemType: 'chart',
				},
			];
		} else if (type === 'dashWeather') {
			editItem.selectItems = [
				{
					id: 'weatherWidget',
					itemType: 'weather',
				},
			];
		} else if (type === 'dashInfo') {
			editItem.selectItems = [
				{
					itemType: 'acc_info',
					id: 'consumption',
				},
				{
					itemType: 'acc_info',
					id: 'allDevices',
				},
				{
					itemType: 'acc_info',
					id: 'offlineDevices',
				},
				{
					itemType: 'acc_info',
					id: 'sharedDevices',
				},
				{
					itemType: 'acc_info',
					id: 'recentlySharedDevices',
				},
				{
					itemType: 'acc_info',
					id: 'hiddenDevices',
				},
			];
		}
	};
</script>

<Stack gap="l">
	{#each DASH_WIDGET_TYPES_LIST as item}
		<div>
			<div class="d-flex align-items-center gap-s mb-m">
				<CircleAvatar
					size="m"
					backgroundColor="color-primary-10"
					icon={`${item.icon} text-color-primary`}
				/>
				<Text styleType="controls-large" class="text-800">{item.title}</Text>
			</div>

			<CardGrid lg={210} sm={160} gridGap="s" noPaddingBottom>
				{#each item.selectCards as card}
					{@const cardRefKey = getDashWidgetSelectListRefKey(card.type, card.cameraVariant)}
					<div bind:this={allCardsRefs[cardRefKey]}>
						<DashSelectCard
							isFocusable
							class="flex-1 min-width-0"
							title={card.title}
							additionalText={card.additionalText}
							icon={card.icon}
							image={card.image || `./images/dash/dash-widget-${card.type}.svg`}
							hideBottomLabelSlot={!card.bottomLabelIcon}
							selected={card.type === 'dashCamera'
								? editItem.widgetType === card.type &&
									editItem.cameraConfig?.variant === card.cameraVariant
								: card.type === editItem.widgetType}
							disabled={card.disabled}
							on:click={() => {
								onSelectWidgetType(card.type, card.cameraVariant);
							}}
							hideTopLeftLabelSlot={!card.premium}
						>
							<LabelV2
								slot="topLeftLabelSlot"
								size="s"
								icon="fal fa-gem"
								noRadiusType="second"
								value={$format('premium', {
									default: 'Premium',
								})}
								backgroundColor="--gradient-premium"
							/>

							<LabelV2
								slot="bottomLabelSlot"
								size="s"
								icon={mapIconSolidWeightToLight(card.bottomLabelIcon)}
								noRadiusType="second"
								backgroundColor="--color-2"
							/>
						</DashSelectCard>
					</div>
				{/each}
			</CardGrid>
		</div>
	{/each}
</Stack>
